import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { 
  Bot, 
  Calendar, 
  Gift, 
  Image, 
  Mail, 
  MessageSquare, 
  Sparkles, 
  Star,
  BellRing
} from "lucide-react"
import { useNavigate } from "react-router-dom"
import { ComingSoonOverlay } from "@/components/ui/coming-soon-overlay"

interface ToolCard {
  title: string
  description: string
  icon: React.ReactNode
  comingSoon?: boolean
  path?: string
}

const tools: ToolCard[] = [
  {
    title: "Volunteer Recruitment Emails",
    description: "AI-generated outreach emails to attract and engage potential volunteers",
    icon: <Mail className="h-5 w-5 text-blue-500" />,
    path: "/dashboard/ai-engine/volunteer-engagement/recruitment-emails"
  },
  {
    title: "Volunteer Thank You Emails",
    description: "Generate heartfelt post-event appreciation messages for your volunteers",
    icon: <Gift className="h-5 w-5 text-pink-500" />,
    path: "/dashboard/ai-engine/volunteer-engagement/thank-you-emails"
  },


  {
    title: "Event Promotion Images",
    description: "Generate eye-catching banners and flyers for volunteer events",
    icon: <Image className="h-5 w-5 text-orange-500" />,
    path: "/dashboard/ai-engine/volunteer-engagement/promotion-images"
  },
  {
    title: "Volunteer Spotlight Templates",
    description: "Create engaging social media posts to recognize volunteers publicly",
    icon: <Star className="h-5 w-5 text-yellow-500" />,
    path: "/dashboard/ai-engine/volunteer-engagement/volunteer-spotlights"
  },
  // {
  //   title: "Automated Reminders & Follow-ups",
  //   description: "Set up pre-scheduled check-ins and event reminders for volunteers",
  //   icon: <BellRing className="h-5 w-5 text-indigo-500" />,
  //   path: "/dashboard/ai-engine/volunteer-engagement/automated-reminders"
  // }
]

export default function VolunteerEngagementPage({ profile, session }: { profile: any | null, session: any | null }) {
  const navigate = useNavigate()

  return (
    <div className="h-full w-full">
      {/* Content */}
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <Bot className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold">AI-Powered Volunteer Engagement</h1>
            <p className="text-muted-foreground">
              Streamline volunteer management and boost engagement with AI assistance
            </p>
          </div>
        </div>

        <div className="relative">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {tools.map((tool) => (
              <Card
                key={tool.title}
                className="group relative overflow-hidden border border-border/50 bg-background/50 backdrop-blur-sm transition-all hover:border-blue-200 hover:shadow-lg"
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="rounded-full bg-blue-50 p-2 transition-colors group-hover:bg-blue-100">
                      {tool.icon}
                    </div>
                    {tool.comingSoon && (
                      <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <CardTitle className="text-xl">{tool.title}</CardTitle>
                  <CardDescription>{tool.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white transition-all hover:from-blue-600 hover:to-purple-600"
                    disabled={tool.comingSoon}
                    onClick={() => tool.path && navigate(tool.path)}
                  >
                    <Sparkles className="mr-2 h-4 w-4" />
                    {tool.comingSoon ? "Coming Soon" : "Get Started"}
                  </Button>
                </CardContent>

                {/* Decorative corner gradients */}
                <div className="absolute -right-6 -top-6 h-12 w-12 rounded-full bg-gradient-to-br from-blue-200 to-transparent opacity-50 blur-lg transition-all group-hover:opacity-100" />
                <div className="absolute -bottom-6 -left-6 h-12 w-12 rounded-full bg-gradient-to-tr from-purple-200 to-transparent opacity-50 blur-lg transition-all group-hover:opacity-100" />
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
